/**
 * Utility functions for managing URL parameters in list pages
 * This ensures consistent behavior across all management pages
 */

/**
 * Initialize state from URL parameters
 * @param {URLSearchParams} searchParams - Current URL search parameters
 * @param {Object} setters - Object containing setter functions for each state
 * @returns {Object} - Object containing initialized values
 */
export const initializeFromUrlParams = (searchParams, setters) => {
  const {
    setCurrentPage,
    setViewMode,
    setSearchItem,
    setSelectedCountry,
    setSelectedLeague,
    setStatus,
    setPageSize
  } = setters;

  const pageFromUrl = searchParams.get('page');
  const viewModeFromUrl = searchParams.get('view');
  const searchFromUrl = searchParams.get('search');
  const countryFromUrl = searchParams.get('country');
  const leagueFromUrl = searchParams.get('league');
  const statusFromUrl = searchParams.get('status');
  const pageSizeFromUrl = searchParams.get('pageSize');

  if (pageFromUrl && !isNaN(pageFromUrl) && parseInt(pageFromUrl) > 0) {
    setCurrentPage(parseInt(pageFromUrl));
  }

  if (viewModeFromUrl && (viewModeFromUrl === 'grid' || viewModeFromUrl === 'list')) {
    setViewMode(viewModeFromUrl);
  }

  if (searchFromUrl) {
    setSearchItem(searchFromUrl);
  }

  if (countryFromUrl) {
    const countryArray = countryFromUrl.split(',').filter(Boolean);
    setSelectedCountry(countryArray);
  }

  if (leagueFromUrl) {
    const leagueArray = leagueFromUrl.split(',').filter(Boolean);
    setSelectedLeague(leagueArray);
  }

  if (statusFromUrl) {
    const statusArray = statusFromUrl.split(',').map(s => parseInt(s)).filter(s => !isNaN(s));
    setStatus(statusArray);
  }

  if (pageSizeFromUrl && !isNaN(pageSizeFromUrl) && parseInt(pageSizeFromUrl) > 0) {
    setPageSize(parseInt(pageSizeFromUrl));
  }

  return {
    page: pageFromUrl ? parseInt(pageFromUrl) : 1,
    view: viewModeFromUrl || 'grid',
    search: searchFromUrl || '',
    country: countryFromUrl ? countryFromUrl.split(',').filter(Boolean) : [],
    league: leagueFromUrl ? leagueFromUrl.split(',').filter(Boolean) : [],
    status: statusFromUrl ? statusFromUrl.split(',').map(s => parseInt(s)).filter(s => !isNaN(s)) : [],
    pageSize: pageSizeFromUrl ? parseInt(pageSizeFromUrl) : 10
  };
};

/**
 * Update URL parameters when search changes
 * @param {URLSearchParams} searchParams - Current URL search parameters
 * @param {Function} setSearchParams - Function to update search parameters
 * @param {string} query - Search query
 * @param {number} page - Current page (default: 1)
 */
export const updateSearchParams = (searchParams, setSearchParams, query, page = 1) => {
  const newSearchParams = new URLSearchParams(searchParams);
  if (query) {
    newSearchParams.set('search', query);
  } else {
    newSearchParams.delete('search');
  }
  newSearchParams.set('page', page.toString());
  setSearchParams(newSearchParams, { replace: true });
};

/**
 * Update URL parameters when page changes
 * @param {URLSearchParams} searchParams - Current URL search parameters
 * @param {Function} setSearchParams - Function to update search parameters
 * @param {number} page - New page number
 */
export const updatePageParams = (searchParams, setSearchParams, page) => {
  const newSearchParams = new URLSearchParams(searchParams);
  newSearchParams.set('page', page.toString());
  setSearchParams(newSearchParams, { replace: true });
};

/**
 * Update URL parameters when filter changes
 * @param {URLSearchParams} searchParams - Current URL search parameters
 * @param {Function} setSearchParams - Function to update search parameters
 * @param {string} filterName - Name of the filter (country, league, status)
 * @param {Array} filterValue - New filter value
 * @param {number} page - Current page (default: 1)
 */
export const updateFilterParams = (searchParams, setSearchParams, filterName, filterValue, page = 1) => {
  const newSearchParams = new URLSearchParams(searchParams);
  if (filterValue && filterValue.length > 0) {
    newSearchParams.set(filterName, filterValue.join(','));
  } else {
    newSearchParams.delete(filterName);
  }
  newSearchParams.set('page', page.toString());
  setSearchParams(newSearchParams, { replace: true });
};

/**
 * Update URL parameters when view mode changes
 * @param {URLSearchParams} searchParams - Current URL search parameters
 * @param {Function} setSearchParams - Function to update search parameters
 * @param {string} viewMode - New view mode (grid or list)
 */
export const updateViewModeParams = (searchParams, setSearchParams, viewMode) => {
  const newSearchParams = new URLSearchParams(searchParams);
  newSearchParams.set('view', viewMode);
  setSearchParams(newSearchParams, { replace: true });
};

/**
 * Clear all search and filter parameters from URL
 * @param {URLSearchParams} searchParams - Current URL search parameters
 * @param {Function} setSearchParams - Function to update search parameters
 */
export const clearAllParams = (searchParams, setSearchParams) => {
  const newSearchParams = new URLSearchParams();
  newSearchParams.set('page', '1');
  newSearchParams.set('view', 'grid');
  setSearchParams(newSearchParams);
};
